/**
 * IANA timezone list generated from Intl.supportedValuesOf('timeZone').
 *
 * A curated POPULAR_TIMEZONES list is shown first in Autocomplete
 * dropdowns; the full list (~400+ entries) is available via search.
 *
 * Each option carries its current UTC offset (computed once at module
 * load from `Date`) for display. Offsets drift twice a year under DST
 * but a stale UI label isn't a correctness problem — the backend
 * persists the IANA name and re-resolves the offset on each use.
 */

export const POPULAR_TIMEZONES: readonly string[] = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Taipei',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Australia/Sydney',
  'Asia/Kolkata',
  'America/Sao_Paulo',
  'America/Toronto',
  'Asia/Dubai',
  'Pacific/Auckland',
  'Asia/Bangkok',
];

export interface TimezoneOption {
  value: string; // IANA name
  label: string; // "(GMT+08:00) Asia/Shanghai"
  offset: string; // "GMT+08:00"
}

/** Current UTC offset string for an IANA timezone (best-effort). */
function getUtcOffset(tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(new Date());
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    return offsetPart?.value ?? '';
  } catch {
    return '';
  }
}

function toOption(tz: string): TimezoneOption {
  const offset = getUtcOffset(tz);
  const city = tz.split('/').slice(1).join('/').replace(/_/g, ' ') || tz;
  const label = offset ? `(${offset}) ${city} — ${tz}` : tz;
  return { value: tz, label, offset };
}

/** Build the full timezone options list. Popular ones first, then the rest. */
export function getTimezoneOptions(): TimezoneOption[] {
  let allZones: string[];
  try {
    // TypeScript lib.es2022 has no supportedValuesOf yet in this tsconfig,
    // so cast through any. Chrome/Firefox/Safari all expose it.
    allZones = (Intl as unknown as {
      supportedValuesOf?: (k: string) => string[];
    }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    allZones = [];
  }

  // Fallback if the runtime lacks Intl.supportedValuesOf.
  if (allZones.length === 0) {
    allZones = [...POPULAR_TIMEZONES];
  }

  const popularSet = new Set<string>(POPULAR_TIMEZONES);
  const others = allZones.filter((tz) => !popularSet.has(tz)).sort();

  return [
    ...POPULAR_TIMEZONES.map(toOption),
    ...others.map(toOption),
  ];
}

/** Cached list — building the array is non-trivial (400+ zones). */
export const timezoneOptions: TimezoneOption[] = getTimezoneOptions();
